import { Component, signal, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { ColumnDef } from '@tanstack/angular-table';
import { TanStackTableComponent } from '../lib/components/tanstack-table/tanstack-table.component';
import { TableTheme, TableIconSet, ARABIC_TABLE_LABELS } from '../lib/components/tanstack-table/tanstack-table.types';

// ========================================
// Data Model
// ========================================

export interface RegionalSalesData {
  id: string;
  region: string;
  country: string;
  product: string;
  category: string;
  quarter: string;
  salesAmount: number;
  units: number;
  profit: number;
  profitMargin: number;
  salesRep: string;
  status: 'Delivered' | 'Pending' | 'Cancelled';
}

// Arabic Sales Data Model (for RTL demo)
export interface ArabicSalesData {
  id: string;
  product: string;
  category: string;
  region: string;
  salesAmount: number;
  units: number;
  status: 'تم التسليم' | 'قيد الانتظار' | 'ملغي';
}

// ========================================
// Component
// ========================================

@Component({
  selector: 'app-root',
  imports: [TanStackTableComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  title = signal('Angular TanStack Table - Feature Playground');

  // ViewChild for accessing table methods
  tableComponent = viewChild<TanStackTableComponent<RegionalSalesData>>('tableComponent');

  // ========================================
  // Theme Configuration
  // ========================================

  // Available themes for the dropdown
  availableThemes: { value: TableTheme; label: string }[] = [
    { value: 'none', label: 'No Theme (Base Layout)' },
    { value: 'material', label: 'Material Design' },
    { value: 'material-dark', label: 'Material Design (Dark)' },
  ];

  // Current theme
  currentTheme = signal<TableTheme>('none');

  // Custom icons example (optional)
  customIcons = signal<Partial<TableIconSet> | undefined>(undefined);

  // Drawer state (open by default)
  drawerOpen = signal(true);

  // Active tab: 'ltr' or 'rtl'
  activeTab = signal<'ltr' | 'rtl'>('ltr');

  // Arabic labels for RTL table
  arabicLabels = ARABIC_TABLE_LABELS;

  // ========================================
  // Feature Configuration Signals
  // ========================================

  // Feature flags
  enableSorting = signal(true);
  enableMultiSort = signal(true);
  enableFiltering = signal(true);
  enableGlobalFilter = signal(true);
  enablePagination = signal(true);
  enableRowSelection = signal(true);
  enableMultiRowSelection = signal(true);
  enableColumnResizing = signal(true);
  enableColumnOrdering = signal(true);
  enableColumnPinning = signal(true);
  enableColumnVisibility = signal(true);
  enableGrouping = signal(true);
  enableRowPinning = signal(false);

  // UI options
  showToolbar = signal(true);
  showGlobalFilter = signal(true);
  showColumnVisibility = signal(true);
  showPagination = signal(true);
  initialAutoFitColumns = signal(true);
  enableAutoRowHeight = signal(false);

  // Pagination
  initialPageSize = signal(100);
  pageSizeOptions = signal([25, 50, 100, 200, 500]);

  // Sample data - start with 1000 rows
  data = signal<RegionalSalesData[]>(this.generateSalesData(1000));

  // Selected rows display
  selectedRowsInfo = signal<string>('No rows selected');

  // Column definitions
  // Note: Select column is now auto-generated when enableRowSelection is true
  columns: ColumnDef<RegionalSalesData, any>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 80,
      enableHiding: false,
    },
    {
      accessorKey: 'region',
      header: 'Region',
      size: 120,
      enableGrouping: true,
    },
    {
      accessorKey: 'country',
      header: 'Country',
      size: 120,
      enableGrouping: true,
    },
    {
      accessorKey: 'product',
      header: 'Product',
      size: 150,
    },
    {
      accessorKey: 'category',
      header: 'Category',
      size: 120,
      enableGrouping: true,
    },
    {
      accessorKey: 'quarter',
      header: 'Quarter',
      size: 100,
      enableGrouping: true,
    },
    {
      accessorKey: 'salesAmount',
      header: 'Sales Amount',
      size: 140,
      cell: (info) => {
        const value = info.getValue() as number;
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
      aggregationFn: 'sum',
      aggregatedCell: (info) => {
        const value = info.getValue() as number;
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      accessorKey: 'units',
      header: 'Units',
      size: 100,
      aggregationFn: 'sum',
    },
    {
      accessorKey: 'profit',
      header: 'Profit',
      size: 130,
      cell: (info) => {
        const value = info.getValue() as number;
        const className = value >= 0 ? 'profit-positive' : 'profit-negative';
        return `<span class="${className}">$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
      },
      aggregationFn: 'sum',
      aggregatedCell: (info) => {
        const value = info.getValue() as number;
        const className = value >= 0 ? 'profit-positive' : 'profit-negative';
        return `<span class="${className}">$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
      },
    },
    {
      accessorKey: 'profitMargin',
      header: 'Profit Margin',
      size: 130,
      cell: (info) => {
        const value = info.getValue() as number;
        return `${(value * 100).toFixed(2)}%`;
      },
      aggregationFn: 'mean',
      aggregatedCell: (info) => {
        const value = info.getValue() as number;
        return `${(value * 100).toFixed(2)}%`;
      },
    },
    {
      accessorKey: 'salesRep',
      header: 'Sales Rep',
      size: 150,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      cell: (info) => {
        const value = info.getValue() as string;
        const className = `status-badge status-${value.toLowerCase()}`;
        return `<span class="${className}">${value}</span>`;
      },
    },
  ];

  // ========================================
  // Arabic RTL Table Configuration
  // ========================================

  // Arabic data
  arabicData = signal<ArabicSalesData[]>(this.generateArabicSalesData(100));

  // Arabic column definitions
  arabicColumns: ColumnDef<ArabicSalesData, any>[] = [
    {
      accessorKey: 'id',
      header: 'رقم الطلب',
      size: 100,
      enableHiding: false,
    },
    {
      accessorKey: 'product',
      header: 'المنتج',
      size: 180,
    },
    {
      accessorKey: 'category',
      header: 'الفئة',
      size: 140,
      enableGrouping: true,
    },
    {
      accessorKey: 'region',
      header: 'المنطقة',
      size: 120,
      enableGrouping: true,
    },
    {
      accessorKey: 'salesAmount',
      header: 'المبلغ',
      size: 140,
      cell: (info) => {
        const value = info.getValue() as number;
        return `${value.toLocaleString('ar-EG')} ج.م`;
      },
      aggregationFn: 'sum',
      aggregatedCell: (info) => {
        const value = info.getValue() as number;
        return `${value.toLocaleString('ar-EG')} ج.م`;
      },
    },
    {
      accessorKey: 'units',
      header: 'الوحدات',
      size: 100,
      aggregationFn: 'sum',
    },
    {
      accessorKey: 'status',
      header: 'الحالة',
      size: 130,
      cell: (info) => {
        const value = info.getValue() as string;
        let className = 'status-badge ';
        if (value === 'تم التسليم') className += 'status-delivered';
        else if (value === 'قيد الانتظار') className += 'status-pending';
        else className += 'status-cancelled';
        return `<span class="${className}">${value}</span>`;
      },
    },
  ];

  // Handle row selection
  handleRowSelection(selectedRows: RegionalSalesData[]) {
    console.log('Selected rows:', selectedRows);
    if (selectedRows.length === 0) {
      this.selectedRowsInfo.set('No rows selected');
    } else {
      const totalSales = selectedRows.reduce((sum, row) => sum + row.salesAmount, 0);
      const totalProfit = selectedRows.reduce((sum, row) => sum + row.profit, 0);
      this.selectedRowsInfo.set(
        `${selectedRows.length} rows selected | Total Sales: $${totalSales.toLocaleString('en-US', { minimumFractionDigits: 2 })} | Total Profit: $${totalProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      );
    }
  }

  // Handle state changes
  handleStateChange(state: any) {
    console.log('Table state changed:', state);
  }

  // Toggle feature methods
  toggleFeature(feature: string) {
    switch (feature) {
      case 'sorting':
        this.enableSorting.update(v => !v);
        break;
      case 'multiSort':
        this.enableMultiSort.update(v => !v);
        break;
      case 'filtering':
        this.enableFiltering.update(v => !v);
        break;
      case 'globalFilter':
        this.enableGlobalFilter.update(v => !v);
        break;
      case 'pagination':
        this.enablePagination.update(v => !v);
        break;
      case 'rowSelection':
        this.enableRowSelection.update(v => !v);
        break;
      case 'multiRowSelection':
        this.enableMultiRowSelection.update(v => !v);
        break;
      case 'columnResizing':
        this.enableColumnResizing.update(v => !v);
        break;
      case 'columnOrdering':
        this.enableColumnOrdering.update(v => !v);
        break;
      case 'columnPinning':
        this.enableColumnPinning.update(v => !v);
        break;
      case 'columnVisibility':
        this.enableColumnVisibility.update(v => !v);
        break;
      case 'grouping':
        this.enableGrouping.update(v => !v);
        break;
      case 'rowPinning':
        this.enableRowPinning.update(v => !v);
        break;
      case 'toolbar':
        this.showToolbar.update(v => !v);
        break;
      case 'showGlobalFilter':
        this.showGlobalFilter.update(v => !v);
        break;
      case 'showColumnVisibility':
        this.showColumnVisibility.update(v => !v);
        break;
      case 'showPagination':
        this.showPagination.update(v => !v);
        break;
      case 'autoFit':
        this.initialAutoFitColumns.update(v => !v);
        break;
      case 'autoRowHeight':
        this.enableAutoRowHeight.update(v => !v);
        break;
    }
  }

  // Add rows to existing data
  addRows(count: number) {
    const currentData = this.data();
    const newRows = this.generateSalesData(count, currentData.length);
    this.data.set([...currentData, ...newRows]);
  }

  // Reset data to 1000 rows
  resetData() {
    this.data.set(this.generateSalesData(1000));
  }

  // Export methods for playground
  exportCSV() {
    const table = this.tableComponent();
    if (table) {
      table.exportData('csv', 'sales-data');
    }
  }

  exportJSON() {
    const table = this.tableComponent();
    if (table) {
      table.exportData('json', 'sales-data');
    }
  }

  // Generate sample sales data
  private generateSalesData(count: number = 1000, startIndex: number = 0): RegionalSalesData[] {
    const regions = ['North America', 'Europe', 'Asia Pacific', 'Latin America'];
    const countries: Record<string, string[]> = {
      'North America': ['USA', 'Canada', 'Mexico'],
      Europe: ['UK', 'Germany', 'France', 'Spain'],
      'Asia Pacific': ['China', 'Japan', 'India', 'Australia'],
      'Latin America': ['Brazil', 'Argentina', 'Chile'],
    };
    const products = [
      'Laptop Pro 15" with Extended Battery Life',
      'Desktop Elite Gaming Edition',
      'Tablet Max Ultra HD Display 12.9"',
      'Smartphone X Pro Max 256GB',
      'Monitor 4K Ultra-Wide Curved 34"',
      'Mechanical Keyboard RGB Backlit',
      'Wireless Mouse Ergonomic Design',
      'Headphones Pro Noise Cancelling',
      'Webcam HD 4K Auto Focus',
      'Speaker System 5.1 Surround Sound',
    ];
    const categories = ['Electronics', 'Computers', 'Accessories', 'Mobile Devices'];
    const quarters = ['Q1 2025', 'Q2 2025', 'Q3 2025', 'Q4 2025'];
    const salesReps = [
      'John Smith (Senior Account Executive)',
      'Sarah Johnson (Regional Sales Director)',
      'Michael Brown (Enterprise Solutions)',
      'Emily Davis (Key Account Manager)',
      'David Wilson (Business Development Lead)',
      'Lisa Anderson (Strategic Partnerships)',
      'Robert Taylor (Global Accounts)',
      'Jennifer Martinez (Corporate Sales)',
    ];
    const statuses: RegionalSalesData['status'][] = ['Delivered', 'Pending', 'Cancelled'];

    const data: RegionalSalesData[] = [];

    for (let i = 0; i < count; i++) {
      const region = regions[Math.floor(Math.random() * regions.length)];
      const country = countries[region][Math.floor(Math.random() * countries[region].length)];
      const product = products[Math.floor(Math.random() * products.length)];
      const category = categories[Math.floor(Math.random() * categories.length)];
      const quarter = quarters[Math.floor(Math.random() * quarters.length)];
      const salesRep = salesReps[Math.floor(Math.random() * salesReps.length)];
      const status = statuses[Math.floor(Math.random() * statuses.length)];

      const units = Math.floor(Math.random() * 1000) + 10;
      const pricePerUnit = Math.random() * 2000 + 100;
      const salesAmount = units * pricePerUnit;
      const costPerUnit = pricePerUnit * (0.5 + Math.random() * 0.3);
      const profit = units * (pricePerUnit - costPerUnit);
      const profitMargin = profit / salesAmount;

      data.push({
        id: `ORD-${String(startIndex + i + 1).padStart(5, '0')}`,
        region,
        country,
        product,
        category,
        quarter,
        salesAmount,
        units,
        profit,
        profitMargin,
        salesRep,
        status,
      });
    }

    return data;
  }

  // Generate Arabic sales data for RTL demo
  private generateArabicSalesData(count: number = 100): ArabicSalesData[] {
    const products = [
      'هاتف ذكي سامسونج',
      'حاسوب محمول ديل',
      'تابلت آيباد برو',
      'شاشة إل جي ٤كي',
      'سماعات سوني لاسلكية',
      'كاميرا كانون احترافية',
      'ساعة أبل الذكية',
      'طابعة إتش بي ليزر',
      'راوتر هواوي فايبر',
      'قرص تخزين سيجيت',
    ];

    const categories = ['إلكترونيات', 'حواسيب', 'إكسسوارات', 'أجهزة منزلية'];

    const regions = [
      'القاهرة',
      'الإسكندرية',
      'الجيزة',
      'الدلتا',
      'الصعيد',
      'البحر الأحمر',
    ];

    const statuses: ArabicSalesData['status'][] = ['تم التسليم', 'قيد الانتظار', 'ملغي'];

    const data: ArabicSalesData[] = [];

    for (let i = 0; i < count; i++) {
      const product = products[Math.floor(Math.random() * products.length)];
      const category = categories[Math.floor(Math.random() * categories.length)];
      const region = regions[Math.floor(Math.random() * regions.length)];
      const status = statuses[Math.floor(Math.random() * statuses.length)];

      const units = Math.floor(Math.random() * 100) + 1;
      const pricePerUnit = Math.random() * 5000 + 500;
      const salesAmount = Math.round(units * pricePerUnit);

      data.push({
        id: `طلب-${String(i + 1).padStart(4, '0')}`,
        product,
        category,
        region,
        salesAmount,
        units,
        status,
      });
    }

    return data;
  }
}
